import React from "react";

import { ProductBackendName } from "Components/Features/CheckPrice/Products/productTypes";
import { CoveragePriceCalculation } from "Components/Features/CheckPrice/Tiles/ProductPriceTile/Coverages/coverages.types";
import { getCollectionOfDittoComponents } from "Components/TextContext";
import { TextComponentNameLevel1 } from "Components/TextContext/textConstants";
import { CarProductCoverages } from "Features/CheckPrice/Products/RiskProducts/VehicleProducts/Car/ProductPrice/FormElements/CarProductCoverages";
import { getExtendedRentalCoverage } from "Features/CheckPrice/Products/RiskProducts/VehicleProducts/Car/ProductPrice/FormElements/helpers";
import { CarCoverage } from "Store/interfaces/products/coverage";
import { useCoveragePriceCalculations } from "Store/priceCalculation/priceCalculationHooks";
import { usePriceCalculationSelector } from "Store/priceCalculation/priceCalculationSelectors";
import { isDefined } from "Utils/functions";
import { useFormikContext } from "formik";
import { CarPriceFormValues } from "../interfaces";
import { LoadingCoverages } from "./LoadingCoverages";

interface Props {
  readonly updatePrices: (field: string, value: unknown) => void;
}

export const Coverages = ({ updatePrices }: Props): React.ReactElement => {
  const formProps = useFormikContext<CarPriceFormValues>();
  const isPriceLoading =
    usePriceCalculationSelector().priceCalculationResult.isLoading;
  const coverages = useCoveragePriceCalculations();
  useUpdateSelectedCoverage(coverages);

  if (isPriceLoading && coverages?.length === 0) {
    return <LoadingCoverages />;
  }

  const { car: productCardTexts } = getCollectionOfDittoComponents(
    TextComponentNameLevel1.COVERAGE_CARD,
  );

  const insuranceTypeVariant = getExtendedRentalCoverage(formProps);

  return (
    <CarProductCoverages
      product={ProductBackendName.CAR}
      priceFilterValue={formProps.values.deductible}
      coverages={coverages}
      coverageStandards={Object.values(CarCoverage)}
      priceIsLoading={isPriceLoading}
      textComponents={productCardTexts}
      insuranceTypeVariant={insuranceTypeVariant}
      updatePrices={updatePrices}
    />
  );
};

const useUpdateSelectedCoverage = (
  coverages: CoveragePriceCalculation[],
): void => {
  const formProps = useFormikContext<CarPriceFormValues>();
  React.useEffect(() => {
    const currentCoverage = formProps.values.coverage;
    const chosenCoverage = coverages.find(
      ([coverage]) => coverage === currentCoverage,
    );

    if (!isDefined(chosenCoverage) && isDefined(currentCoverage)) {
      const correspondingCoverage = findCorrespondingCoverage(currentCoverage);
      const defaultCoverage = coverages.find(
        ([coverage]) => coverage === correspondingCoverage,
      )?.[0];

      const isValid =
        coverages.filter(([coverage]) => coverage === defaultCoverage)
          .length === 1;

      formProps.setFieldValue(
        "coverage",
        isValid ? defaultCoverage : undefined,
      );
    }
  }, [coverages]);
};

function findCorrespondingCoverage(
  currentCoverage: CarCoverage,
): CarCoverage | undefined {
  switch (currentCoverage) {
    case CarCoverage.AUTO_FULL_OWN_DAMAGE_INCLUDING_RENTALCAR:
      return CarCoverage.AUTO_FULL_OWN_DAMAGE;
    case CarCoverage.AUTO_FULL_OWN_DAMAGE:
      return CarCoverage.AUTO_FULL_OWN_DAMAGE_INCLUDING_RENTALCAR;
    case CarCoverage.AUTO_FULL_SUPER_V2_INCLUDING_RENTALCAR:
      return CarCoverage.AUTO_FULL_SUPER_V2;
    case CarCoverage.AUTO_FULL_SUPER_V2:
      return CarCoverage.AUTO_FULL_SUPER_V2_INCLUDING_RENTALCAR;
    default:
      return undefined;
  }
}
